import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Game, Player } from '../types';

const API_URL = process.env.REACT_APP_API_URL || '';

const COLORS = [
  '#E53935', '#D81B60', '#8E24AA', '#5E35B1', 
  '#3949AB', '#1E88E5', '#00ACC1', '#00897B',
  '#43A047', '#7CB342', '#FDD835', '#FB8C00'
];

const GameSetup: React.FC = () => {
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get('gameId');
  const [game, setGame] = useState<Game | null>(null);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [gameName, setGameName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (gameId) {
      fetchGame();
    }
  }, [gameId]);

  const fetchGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}`);
      const data = await res.json();
      setGame(data);
      setGameName(data.name);
    } catch (error) {
      console.error('Error fetching game:', error);
    }
  };

  const updateGameName = async () => {
    if (!game || !gameName.trim()) return;
    try {
      await fetch(`${API_URL}/api/games/${game.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: gameName })
      });
      fetchGame();
    } catch (error) {
      console.error('Error updating game:', error);
    }
  };

  const addPlayer = async () => {
    if (!game || !newPlayerName.trim()) return;
    try {
      const colorIndex = game.players.length % COLORS.length;
      await fetch(`${API_URL}/api/games/${game.id}/players`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: newPlayerName,
          color: COLORS[colorIndex]
        })
      });
      setNewPlayerName('');
      fetchGame();
    } catch (error) {
      console.error('Error adding player:', error);
    }
  };

  const removePlayer = async (playerId: string) => {
    if (!game) return;
    try {
      await fetch(`${API_URL}/api/games/${game.id}/players/${playerId}`, {
        method: 'DELETE'
      });
      fetchGame();
    } catch (error) {
      console.error('Error removing player:', error);
    }
  };

  const startGame = async () => {
    if (!game || game.players.length < 3) {
      alert('Je hebt minimaal 3 spelers nodig om te starten!');
      return;
    }
    try {
      await fetch(`${API_URL}/api/games/${game.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'active' })
      });
      navigate(`/game/${game.id}`);
    } catch (error) {
      console.error('Error starting game:', error);
    }
  };

  if (!game) {
    return <div className="page loading">Laden...</div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Spel Instellen</h1>
        <p>Voeg de kandidaten toe en start het spel</p>
      </div>

      <div className="card">
        <h2>Spelnaam</h2>
        <div className="form-group">
          <input
            type="text"
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
            onBlur={updateGameName}
            placeholder="Bijv. Wie is de Mol 2026"
          />
        </div>
      </div>

      <div className="card">
        <h2>Kandidaten ({game.players.length})</h2>
        
        <div className="form-group" style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            value={newPlayerName}
            onChange={(e) => setNewPlayerName(e.target.value)}
            placeholder="Naam kandidaat"
            onKeyPress={(e) => e.key === 'Enter' && addPlayer()}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary" onClick={addPlayer}>
            Toevoegen
          </button>
        </div>

        {game.players.length === 0 ? (
          <div className="empty-state">
            <p>Voeg kandidaten toe om te beginnen</p>
          </div>
        ) : (
          <ul className="player-list">
            {game.players.map((player: Player) => (
              <li key={player.id} className="player-item">
                <div className="player-info">
                  <div 
                    className="player-avatar"
                    style={{ backgroundColor: player.color }}
                  >
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="player-name">{player.name}</span>
                </div>
                <button 
                  className="btn btn-danger btn-small"
                  onClick={() => removePlayer(player.id)}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="nav-bar" style={{ marginTop: '20px' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          ← Terug
        </button>
        <button 
          className="btn btn-accent" 
          onClick={startGame}
          disabled={game.players.length < 3}
          style={{ flex: 1 }}
        >
          Start Spel →
        </button>
      </div>
    </div>
  );
};

export default GameSetup;
